/**
 * Makes React survive a DOM that something else rewrote underneath it.
 *
 * Browser translation (and any extension that rewrites text) replaces React's text
 * nodes with <font> wrappers of its own. React still holds references to the original
 * nodes, so the next unmount calls removeChild on a node that is no longer a child of
 * the parent React remembers — and the DOM throws:
 *
 *   NotFoundError: Failed to execute 'removeChild' on 'Node':
 *   The node to be removed is not a child of this node.
 *
 * That exception escapes reconciliation and takes the whole app down. Wrapping the two
 * mutation methods so they degrade instead of throwing turns a fatal crash into a
 * cosmetic glitch: the page may look slightly off, but it keeps working.
 *
 * The <meta name="google" content="notranslate"> tag already stops Chrome from
 * OFFERING to translate, but it cannot override a preference the user saved earlier
 * ("always translate this language"), and third-party extensions ignore it entirely.
 * This guard is what covers those users.
 *
 * Must be installed before the first render.
 *
 * See https://martijnhols.nl/blog/everything-about-google-translate-crashing-react
 */

const GUARD_FLAG = '__c1do1DomGuardInstalled';

// Kept at module scope so uninstall can put the real methods back. Deleting the
// patched properties would not restore them — removeChild and insertBefore are own
// properties of Node.prototype, so deleting leaves the DOM without them entirely.
let nativeRemoveChild = null;
let nativeInsertBefore = null;

/**
 * @param {{ onGuarded?: (method: string) => void }} [options]
 *   onGuarded fires the first time each method has to step in, so callers can report
 *   that this session is running against a mutated DOM without flooding telemetry.
 * @returns {boolean} true if the guard was installed by this call
 */
export const installDomMutationGuard = ({ onGuarded } = {}) => {
  if (typeof Node === 'undefined' || !Node.prototype) return false;
  if (Node.prototype[GUARD_FLAG]) return false;

  const reported = {};
  const report = (method) => {
    if (reported[method]) return;
    reported[method] = true;
    if (typeof onGuarded === 'function') {
      try {
        onGuarded(method);
      } catch {
        // Telemetry must never be the reason a render fails.
      }
    }
  };

  const originalRemoveChild = Node.prototype.removeChild;
  nativeRemoveChild = originalRemoveChild;
  Node.prototype.removeChild = function removeChild(child) {
    if (child && child.parentNode !== this) {
      // Someone else already moved it. React wanted it gone; it is gone.
      report('removeChild');
      return child;
    }
    return originalRemoveChild.apply(this, arguments);
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  nativeInsertBefore = originalInsertBefore;
  Node.prototype.insertBefore = function insertBefore(newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) {
      // The anchor React wanted to insert before is no longer here. Appending keeps
      // the node in the tree; only its position may be off.
      report('insertBefore');
      return originalInsertBefore.call(this, newNode, null);
    }
    return originalInsertBefore.apply(this, arguments);
  };

  Node.prototype[GUARD_FLAG] = true;
  return true;
};

/** Test helper: puts the native methods back. */
export const uninstallDomMutationGuard = () => {
  if (typeof Node === 'undefined' || !Node.prototype[GUARD_FLAG]) return;
  if (nativeRemoveChild) Node.prototype.removeChild = nativeRemoveChild;
  if (nativeInsertBefore) Node.prototype.insertBefore = nativeInsertBefore;
  nativeRemoveChild = null;
  nativeInsertBefore = null;
  delete Node.prototype[GUARD_FLAG];
};
